import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Type, Image, Save, Upload, Eye, EyeOff, Palette, Plus, Trash2, MailCheck
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';

export default function ContentManager() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscribers, setSubscribers] = useState([]);

  const load = useCallback(() => {
    api.getSiteContent()
      .then(res => setContent(res.data))
      .catch(() => toast.error('Failed to load site content'))
      .finally(() => setLoading(false));
    api.getNewsletterSubscribers()
      .then(res => setSubscribers(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSiteContent(content);
      toast.success('Site content saved! Changes are live.');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (section, callback) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const res = await api.uploadSiteImage(file, section);
        callback(res.data.image_url);
        toast.success('Image uploaded!');
      } catch (err) {
        toast.error('Upload failed');
      }
    };
    input.click();
  };

  const updateField = (path, value) => {
    const newContent = { ...content };
    const keys = path.split('.');
    let obj = newContent;
    for (let i = 0; i < keys.length - 1; i++) {
      if (Array.isArray(obj[keys[i]])) {
        obj = obj[keys[i]];
      } else {
        obj = obj[keys[i]];
      }
    }
    obj[keys[keys.length - 1]] = value;
    setContent({ ...newContent });
  };

  const updateBlend = (index, field, value) => {
    const newContent = { ...content };
    newContent.blends[index][field] = value;
    setContent({ ...newContent });
  };

  const updateBadge = (index, field, value) => {
    const newContent = { ...content };
    newContent.trust_badges[index][field] = value;
    setContent({ ...newContent });
  };

  const addBlend = () => {
    const newContent = { ...content };
    newContent.blends.push({ name: 'NEW BLEND', cursive: 'Description', desc: '', hairType: '', color: '#4A5D3A', image: '' });
    setContent({ ...newContent });
  };

  const removeBlend = (index) => {
    const newContent = { ...content };
    newContent.blends.splice(index, 1);
    setContent({ ...newContent });
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading content...</div>;
  if (!content) return <div className="text-center py-12 text-gray-500">No content found</div>;

  return (
    <div className="space-y-6" data-testid="content-manager">
      {/* Save Button - Always Visible */}
      <div className="flex justify-between items-center sticky top-0 bg-[#FAFAFA] py-2 z-10">
        <h2 className="text-xl font-semibold">Site Content Manager</h2>
        <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#4A5D3A' }} data-testid="save-content-btn">
          <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {/* Banner */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Type size={16} /> Opening Soon Banner</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Label>Visible</Label>
            <Switch checked={content.banner?.visible} onCheckedChange={(v) => updateField('banner.visible', v)} data-testid="banner-visible-toggle" />
          </div>
          <div><Label>Banner Text</Label><Input value={content.banner?.text || ''} onChange={e => updateField('banner.text', e.target.value)} data-testid="banner-text-input" /></div>
        </CardContent>
      </Card>

      {/* Hero Section */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Image size={16} /> Hero Section</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Label</Label><Input value={content.hero?.label || ''} onChange={e => updateField('hero.label', e.target.value)} /></div>
          <div><Label>Title Line 1</Label><Input value={content.hero?.title_line1 || ''} onChange={e => updateField('hero.title_line1', e.target.value)} data-testid="hero-title-input" /></div>
          <div><Label>Title Cursive Line</Label><Input value={content.hero?.title_cursive || ''} onChange={e => updateField('hero.title_cursive', e.target.value)} /></div>
          <div><Label>Description</Label><Textarea value={content.hero?.description || ''} onChange={e => updateField('hero.description', e.target.value)} rows={3} data-testid="hero-desc-input" /></div>
          <div className="flex items-center gap-3">
            <Label>Hero Image</Label>
            {content.hero?.image && <img src={content.hero.image} alt="" className="w-20 h-14 object-cover rounded" />}
            <Button variant="outline" size="sm" onClick={() => handleImageUpload('hero', (url) => updateField('hero.image', url))} data-testid="hero-image-upload">
              <Upload size={14} className="mr-1" /> Change Image
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Blends */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-base flex items-center gap-2"><Palette size={16} /> Blends / Products</CardTitle>
            <Button variant="outline" size="sm" onClick={addBlend} data-testid="add-blend-btn"><Plus size={14} className="mr-1" /> Add Blend</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {content.blends?.map((blend, i) => (
            <div key={i} className="border rounded-lg p-4 space-y-3 relative" data-testid={`blend-editor-${i}`}>
              <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => removeBlend(i)}><Trash2 size={12} /></Button>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Name</Label><Input value={blend.name} onChange={e => updateBlend(i, 'name', e.target.value)} /></div>
                <div><Label>Cursive Text</Label><Input value={blend.cursive} onChange={e => updateBlend(i, 'cursive', e.target.value)} /></div>
                <div><Label>Hair Type</Label><Input value={blend.hairType} onChange={e => updateBlend(i, 'hairType', e.target.value)} /></div>
              </div>
              <div><Label>Description</Label><Textarea value={blend.desc} onChange={e => updateBlend(i, 'desc', e.target.value)} rows={2} /></div>
              <div className="flex items-center gap-3">
                <div><Label>Color</Label><Input type="color" value={blend.color} onChange={e => updateBlend(i, 'color', e.target.value)} className="w-20 h-9" /></div>
                {blend.image && <img src={blend.image} alt="" className="w-14 h-18 object-cover rounded" />}
                <Button variant="outline" size="sm" onClick={() => handleImageUpload(`blend-${i}`, (url) => updateBlend(i, 'image', url))}>
                  <Upload size={14} className="mr-1" /> Image
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Find Your Blend Section */}
      <Card>
        <CardHeader><CardTitle className="text-base">Find Your Blend Section</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Title</Label><Input value={content.find_blend?.title || ''} onChange={e => updateField('find_blend.title', e.target.value)} /></div>
          <div><Label>Cursive Title</Label><Input value={content.find_blend?.title_cursive || ''} onChange={e => updateField('find_blend.title_cursive', e.target.value)} /></div>
          <div><Label>Description</Label><Textarea value={content.find_blend?.description || ''} onChange={e => updateField('find_blend.description', e.target.value)} rows={2} /></div>
          <div className="flex items-center gap-3">
            <Label>Image</Label>
            {content.find_blend?.image && <img src={content.find_blend.image} alt="" className="w-20 h-14 object-cover rounded" />}
            <Button variant="outline" size="sm" onClick={() => handleImageUpload('find-blend', (url) => updateField('find_blend.image', url))}>
              <Upload size={14} className="mr-1" /> Change Image
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Trust Badges */}
      <Card>
        <CardHeader><CardTitle className="text-base">Trust Badges</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {content.trust_badges?.map((badge, i) => (
            <div key={i} className="grid grid-cols-2 gap-3">
              <div><Label>Title</Label><Input value={badge.title} onChange={e => updateBadge(i, 'title', e.target.value)} /></div>
              <div><Label>Subtitle</Label><Input value={badge.subtitle} onChange={e => updateBadge(i, 'subtitle', e.target.value)} /></div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input value={content.contact?.phone || ''} onChange={e => updateField('contact.phone', e.target.value)} data-testid="contact-phone-edit" /></div>
            <div><Label>Email</Label><Input value={content.contact?.email || ''} onChange={e => updateField('contact.email', e.target.value)} /></div>
            <div><Label>Location</Label><Input value={content.contact?.location || ''} onChange={e => updateField('contact.location', e.target.value)} /></div>
            <div><Label>Social Handle</Label><Input value={content.contact?.social_handle || ''} onChange={e => updateField('contact.social_handle', e.target.value)} /></div>
            <div><Label>Instagram URL</Label><Input value={content.contact?.instagram || ''} onChange={e => updateField('contact.instagram', e.target.value)} /></div>
            <div><Label>Facebook URL</Label><Input value={content.contact?.facebook || ''} onChange={e => updateField('contact.facebook', e.target.value)} /></div>
            <div><Label>YouTube URL</Label><Input value={content.contact?.youtube || ''} onChange={e => updateField('contact.youtube', e.target.value)} /></div>
            <div><Label>Shopify Store URL</Label><Input value={content.shopify_url || ''} onChange={e => updateField('shopify_url', e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <Card>
        <CardHeader><CardTitle className="text-base">Footer</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Tagline</Label><Input value={content.footer?.tagline || ''} onChange={e => updateField('footer.tagline', e.target.value)} /></div>
          <div><Label>Cursive Tagline</Label><Input value={content.footer?.tagline_cursive || ''} onChange={e => updateField('footer.tagline_cursive', e.target.value)} /></div>
        </CardContent>
      </Card>

      {/* Newsletter Subscribers */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MailCheck size={16} /> Newsletter Subscribers ({subscribers.length})</CardTitle></CardHeader>
        <CardContent>
          {subscribers.length === 0 ? (
            <p className="text-gray-400 text-sm">No subscribers yet</p>
          ) : (
            <div className="space-y-1">
              {subscribers.map((s, i) => (
                <div key={i} className="flex justify-between items-center py-1.5 border-b last:border-0 text-sm">
                  <span>{s.email}</span>
                  <span className="text-xs text-gray-400">{new Date(s.subscribed_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Founders / Our Story */}
      <Card data-testid="content-founders">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Type size={16} /> Founders / Our Story Page
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Page Title</Label>
              <Input value={content?.founders?.page_title || ''} onChange={e => setContent({ ...content, founders: { ...content?.founders, page_title: e.target.value } })} data-testid="founders-page-title" />
            </div>
            <div>
              <Label>Page Subtitle</Label>
              <Input value={content?.founders?.page_subtitle || ''} onChange={e => setContent({ ...content, founders: { ...content?.founders, page_subtitle: e.target.value } })} data-testid="founders-page-subtitle" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Founder Name</Label>
              <Input value={content?.founders?.founder_name || ''} onChange={e => setContent({ ...content, founders: { ...content?.founders, founder_name: e.target.value } })} data-testid="founders-name-input" />
            </div>
            <div>
              <Label>Founder Title</Label>
              <Input value={content?.founders?.founder_title || ''} onChange={e => setContent({ ...content, founders: { ...content?.founders, founder_title: e.target.value } })} data-testid="founders-title-input" />
            </div>
          </div>
          <div>
            <Label>Founder Photo</Label>
            <div className="flex items-center gap-3 mt-1">
              {content?.founders?.founder_photo && (
                <img src={content.founders.founder_photo} alt="Founder" className="w-20 h-20 object-cover rounded" />
              )}
              <input type="file" accept="image/*" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                try {
                  const res = await api.uploadSiteImage(file, 'founders');
                  const url = res.data.url;
                  setContent({ ...content, founders: { ...content?.founders, founder_photo: url } });
                  toast.success('Photo uploaded');
                } catch { toast.error('Upload failed'); }
              }} data-testid="founders-photo-upload" />
            </div>
          </div>
          <div>
            <Label>Founder Bio (use line breaks for paragraphs)</Label>
            <Textarea rows={6} value={content?.founders?.founder_bio || ''} onChange={e => setContent({ ...content, founders: { ...content?.founders, founder_bio: e.target.value } })} data-testid="founders-bio-input" />
          </div>
          <div>
            <Label>Mission Title</Label>
            <Input value={content?.founders?.mission_title || ''} onChange={e => setContent({ ...content, founders: { ...content?.founders, mission_title: e.target.value } })} data-testid="founders-mission-title" />
          </div>
          <div>
            <Label>Mission Text (use line breaks for paragraphs)</Label>
            <Textarea rows={6} value={content?.founders?.mission_text || ''} onChange={e => setContent({ ...content, founders: { ...content?.founders, mission_text: e.target.value } })} data-testid="founders-mission-text" />
          </div>
          <div>
            <Label>Core Values</Label>
            <div className="space-y-2 mt-2">
              {(content?.founders?.values || []).map((v, i) => (
                <div key={i} className="flex gap-2">
                  <Input className="w-1/3" placeholder="Title" value={v.title} onChange={e => {
                    const vals = [...(content?.founders?.values || [])];
                    vals[i] = { ...vals[i], title: e.target.value };
                    setContent({ ...content, founders: { ...content?.founders, values: vals } });
                  }} />
                  <Input className="flex-1" placeholder="Description" value={v.text} onChange={e => {
                    const vals = [...(content?.founders?.values || [])];
                    vals[i] = { ...vals[i], text: e.target.value };
                    setContent({ ...content, founders: { ...content?.founders, values: vals } });
                  }} />
                  <Button size="sm" variant="destructive" onClick={() => {
                    const vals = (content?.founders?.values || []).filter((_, idx) => idx !== i);
                    setContent({ ...content, founders: { ...content?.founders, values: vals } });
                  }}><Trash2 size={12} /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => {
                const vals = [...(content?.founders?.values || []), { title: '', text: '' }];
                setContent({ ...content, founders: { ...content?.founders, values: vals } });
              }}><Plus size={12} className="mr-1" /> Add Value</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save */}
      <div className="text-center pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" style={{ backgroundColor: '#4A5D3A' }} data-testid="save-content-btn-bottom">
          <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
}
